/**
 * useDesignVersion — runtime version gate for v2/v3 design system.
 *
 * Primary: reads `technical.v3_design` flag from FlagContext (resolved
 * from config worker's three-layer cascade).
 *
 * Fallback: VITE_DESIGN_VERSION env var for builds without config worker
 * (dev, tests, FBR deploys before flag adoption).
 *
 * `isV3()` is for non-React contexts (module-level guards, imports).
 * It can only read the env var — no React context outside the tree.
 *
 * Rollback: set operator override `technical.v3_design=false` via admin
 * API, or redeploy with VITE_DESIGN_VERSION=v2.
 */

import { useFlag, useFlagLoaded } from '../contexts/FlagContext';

export type DesignVersion = 'v2' | 'v3';

// Build-time env var — fallback for non-React contexts and dev/test
const envVersion: DesignVersion =
  (import.meta.env.VITE_DESIGN_VERSION as DesignVersion) || 'v2';

export function useDesignVersion(): DesignVersion {
  const flagV3 = useFlag('technical.v3_design');
  const loaded = useFlagLoaded();
  // If flag system loaded from API, flag is authoritative (even if false).
  // If not loaded (API unreachable), fall back to env var.
  if (loaded) return flagV3 ? 'v3' : 'v2';
  return envVersion;
}

export function isV3(): boolean {
  // Module-level only — env var, no React context
  return envVersion === 'v3';
}
